import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { Database, ArrowRight, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useQueryStore } from '../../../store/queryStore';
import { AnimatedNumber } from '../../ui/animated-number';
import { useResponsive } from '../../../hooks/use-responsive';
import Ripple from '../../ui/ripple';

export const SavedApiKeyCard = ({
  loading,
  forceRender = false
}) => {
  // 确保所有钩子始终在组件顶部调用，不受条件影响
  const { isMobile } = useResponsive();
  
  // 使用稳定的选择器，分别获取数据和方法，添加更严格的类型检查
  const savedKeys = useQueryStore((state) => {
    if (!state || !Array.isArray(state.savedKeys)) {
      return [];
    }
    return state.savedKeys;
  });
  
  const currentKeyIndex = useQueryStore((state) => {
    if (!state || typeof state.currentKeyIndex !== 'number') {
      return 0;
    }
    return state.currentKeyIndex;
  });
  
  const setCurrentKeyIndex = useQueryStore((state) => state?.setCurrentKeyIndex);
  const performQueryWithKey = useQueryStore((state) => state?.performQueryWithKey);
  const removeKeyFromList = useQueryStore((state) => state?.removeKeyFromList);

  // 使用useMemo来确保计算值的稳定性，添加额外的安全检查
  const computedData = useMemo(() => {
    const safeCurrentIndex = Math.max(0, Math.min(currentKeyIndex, savedKeys.length - 1));
    const currentKey = savedKeys[safeCurrentIndex] || null;
    
    return {
      safeCurrentIndex,
      currentKey,
      hasKeys: savedKeys.length > 0
    };
  }, [savedKeys, currentKeyIndex]);

  const { safeCurrentIndex, currentKey, hasKeys } = computedData;

  // 确保dragControls始终被调用
  const dragControls = useDragControls();

  // 确保所有useCallback都在组件顶部，不受条件影响
  const handlePrevKey = useCallback((e) => {
    e?.stopPropagation();
    if (!hasKeys) return;
    const currentIndex = Math.max(0, Math.min(currentKeyIndex, savedKeys.length - 1));
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : savedKeys.length - 1;
    if (setCurrentKeyIndex) setCurrentKeyIndex(prevIndex);
  }, [currentKeyIndex, savedKeys.length, setCurrentKeyIndex, hasKeys]);

  const handleNextKey = useCallback((e) => {
    e?.stopPropagation();
    if (!hasKeys) return;
    const currentIndex = Math.max(0, Math.min(currentKeyIndex, savedKeys.length - 1));
    const nextIndex = currentIndex < savedKeys.length - 1 ? currentIndex + 1 : 0;
    if (setCurrentKeyIndex) setCurrentKeyIndex(nextIndex);
  }, [currentKeyIndex, savedKeys.length, setCurrentKeyIndex, hasKeys]);

  // 处理拖拽切换（仅移动端）
  const handleDragEnd = useCallback((event, info) => {
    if (!isMobile || savedKeys.length < 2) return;
    
    const threshold = 50; // 拖拽阈值
    
    if (info?.offset?.x > threshold) {
      // 向右拖拽 - 上一个
      handlePrevKey(event);
    } else if (info?.offset?.x < -threshold) {
      // 向左拖拽 - 下一个
      handleNextKey(event);
    }
  }, [isMobile, savedKeys.length, handlePrevKey, handleNextKey]);

  // 处理删除密钥
  const handleDeleteKey = useCallback((e) => {
    e?.stopPropagation();
    if (currentKey?.id && removeKeyFromList && window.confirm(`确定要删除此Key吗？`)) {
      removeKeyFromList(currentKey.id);
    }
  }, [currentKey?.id, removeKeyFromList]);

  // 使用useCallback稳定化onClick处理函数
  const handleCardClick = useCallback(async () => {
    if (!loading && currentKey?.id && performQueryWithKey) {
      try {
        await performQueryWithKey(currentKey.id);
      } catch (error) {
        console.error('查询失败:', error);
      }
    }
  }, [loading, currentKey?.id, performQueryWithKey]);

  // 渲染空状态（没有密钥时）- 完全隐藏组件但保持DOM结构
  const renderEmptyState = () => ({
    className: "hidden", // 完全隐藏，不占据网格空间
    onClick: () => {},
    background: null,
    children: null,
  });

  // 渲染有密钥的状态
  const renderWithKeys = () => ({
    className: "col-span-1 row-span-1 group relative overflow-hidden",
    onClick: handleCardClick,
    background: (
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
        {/* 移动端取消右上角图标 */}
        {!isMobile && (
          <>
            <Ripple 
              mainCircleSize={120}
              mainCircleOpacity={0.15}
              numCircles={6}
              className="opacity-60 dark:opacity-40"
            />
            {/* 分页指示器 - 只在有多个密钥时显示 */}
            {savedKeys.length >= 2 && (
              <div className="absolute top-4 left-4 flex gap-1 z-10">
                {savedKeys.map((_, index) => (
                  <motion.div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === safeCurrentIndex 
                        ? 'bg-gray-700 dark:bg-gray-300' 
                        : 'bg-gray-400/50 dark:bg-gray-500/30'
                    }`}
                    animate={{ scale: index === safeCurrentIndex ? 1.2 : 1 }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    ),
    children: (
      <motion.div 
        className={`relative z-20 h-full flex flex-col justify-between ${isMobile ? 'p-4' : 'p-6'}`}
        drag={isMobile && savedKeys.length >= 2 ? "x" : false}
        dragConstraints={isMobile && savedKeys.length >= 2 ? { left: 0, right: 0 } : {}}
        dragElastic={isMobile && savedKeys.length >= 2 ? 0.2 : 0}
        onDragEnd={isMobile && savedKeys.length >= 2 ? handleDragEnd : undefined}
        whileDrag={isMobile && savedKeys.length >= 2 ? { scale: 0.95 } : {}}
        dragControls={isMobile && savedKeys.length >= 2 ? dragControls : undefined}
        style={{
          // 确保桌面端不受拖拽影响
          pointerEvents: 'auto',
          userSelect: isMobile ? 'none' : 'auto'
        }}
      >
        {isMobile ? (
          // 移动端引导式布局
          <>
            {/* 顶部：标签和分页 */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wider uppercase">
                历史记录
              </div>
              {/* 移动端分页控制和拖拽提示 */}
              {savedKeys.length >= 2 && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <motion.button
                      onClick={handlePrevKey}
                      className="p-1 rounded-full bg-gray-500/20 hover:bg-gray-500/30 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <ChevronLeft className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                    </motion.button>
                    <span className="text-xs text-gray-500 mx-1">
                      {safeCurrentIndex + 1}/{savedKeys.length}
                    </span>
                    <motion.button
                      onClick={handleNextKey}
                      className="p-1 rounded-full bg-gray-500/20 hover:bg-gray-500/30 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
            
            {/* 主要内容 */}
            <div className="flex-1 flex flex-col justify-center">
              {/* API Key 显示 */}
              <motion.div 
                className="bg-black/5 dark:bg-white/5 rounded-lg p-3 mb-2"
                key={safeCurrentIndex} // 重要：让每次切换都有动画
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <code className="text-xs font-mono text-gray-600 dark:text-gray-300 block truncate">
                  {currentKey.apikey ? 
                    `${currentKey.apikey.substring(0, 8)}•••••${currentKey.apikey.slice(-4)}` 
                    : 'sk-•••••••••••'
                  }
                </code>
              </motion.div>

              {/* 服务和额度信息 */}
              <motion.div 
                className="text-xs text-gray-500 dark:text-gray-400 space-y-1"
                key={`info-${safeCurrentIndex}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {currentKey.service?.name && (
                  <div className="text-sm font-bold">服务: <span className="font-black text-gray-700 dark:text-gray-200">{currentKey.service.name.replace('Key查询', '') || '全模型'}</span></div>
                )}
                {currentKey.queryData && (
                  <div>
                    余额: {currentKey.queryData.balance === 100000000 ? (
                      <span className="font-medium text-gray-500 dark:text-gray-400">无限制</span>
                    ) : (
                      <AnimatedNumber
                        value={(currentKey.queryData.balance || 0) - (currentKey.queryData.usage || 0)}
                        prefix="$"
                        decimals={2}
                        duration={1}
                        className="font-medium text-gray-500 dark:text-gray-400"
                      />
                    )}
                  </div>
                )}
              </motion.div>
            </div>
            
            {/* 移动端取消底部查询文字 */}
          </>
        ) : (
          // 桌面端原有布局
          <>
            {/* 顶部标签和分页控制 */}
            <div className="flex items-center justify-between">
              <div className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wider uppercase">
                {savedKeys.length >= 2 ? `History Key (${safeCurrentIndex + 1}/${savedKeys.length})` : 'SAVED API KEY'}
              </div>
              
              {/* 分页控制按钮 */}
              {savedKeys.length >= 2 && (
                <div className="flex items-center gap-1 relative z-30">
                  <motion.button
                    onClick={handlePrevKey}
                    className="p-1 rounded-full bg-gray-500/20 hover:bg-gray-500/30 transition-colors relative z-40"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <ChevronLeft className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                  </motion.button>
                  <motion.button
                    onClick={handleNextKey}
                    className="p-1 rounded-full bg-gray-500/20 hover:bg-gray-500/30 transition-colors relative z-40"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                  </motion.button>
                </div>
              )}
            </div>
            
            {/* 主要内容 */}
            <div className="flex-1 flex flex-col justify-center">
              <h3 className="text-gray-800 dark:text-gray-200 text-lg font-bold leading-tight mb-3">
                历史记录
              </h3>
              
              {/* API Key 显示 */}
              <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3 mb-2 border border-gray-200/50 dark:border-gray-600/50 transition-all duration-200 group-hover:bg-black/10 dark:group-hover:bg-white/10">
                <code className="text-xs font-mono text-gray-600 dark:text-gray-300 block truncate">
                  {currentKey.apikey ? 
                    `${currentKey.apikey.substring(0, 8)}•••••${currentKey.apikey.slice(-4)}` 
                    : 'sk-•••••••••••'
                  }
                </code>
              </div>

              {/* 服务信息 */}
              {currentKey.service?.name && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">
                  服务: <span className="font-black text-base text-gray-700 dark:text-gray-200">{currentKey.service.name.replace('Key查询', '') || '全模型'}</span>
                </div>
              )}

              {/* 可用额度信息 */}
              {currentKey.queryData && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  可用额度: {currentKey.queryData.balance === 100000000 ? (
                    <span className="font-medium">无限制</span>
                  ) : (
                    <AnimatedNumber
                      value={(currentKey.queryData.balance || 0) - (currentKey.queryData.usage || 0)}
                      prefix="$"
                      decimals={2}
                      duration={1}
                      className="font-medium"
                    />
                  )}
                </div>
              )}
            </div>
            
            {/* 底部操作区 */}
            <div className="flex items-center justify-between">
              {/* 查询按钮 */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                <ArrowRight className="h-4 w-4" />
                快速查询
              </div>
              
              {/* 删除按钮 */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <motion.button
                  onClick={handleDeleteKey}
                  className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-800 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="删除密钥"
                >
                  <Trash2 className="h-3 w-3" />
                </motion.button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    ),
  });

  // 根据是否有密钥返回相应的渲染结果
  // 始终渲染组件，避免钩子数量不一致的问题
  return currentKey ? renderWithKeys() : renderEmptyState();
};